import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  TextField,
  Alert,
  Divider,
} from '@mui/material'
import {
  Edit as EditIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { InventoryService } from '../../services/inventoryService'
import { LowStockVariant } from '../../types/inventory'

interface LowStockAlertsDialogProps {
  open: boolean
  lowStockVariants: LowStockVariant[]
  onClose: () => void
  onRefresh: () => void
}

const LowStockAlertsDialog: React.FC<LowStockAlertsDialogProps> = ({
  open,
  lowStockVariants,
  onClose,
  onRefresh,
}) => {
  const [editingThreshold, setEditingThreshold] = useState<string | null>(null)
  const [newThreshold, setNewThreshold] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const handleEditThreshold = (variant: LowStockVariant) => {
    setEditingThreshold(variant.variant_id)
    setNewThreshold(variant.minimum_stock.toString())
  }

  const handleSaveThreshold = async (variantId: string) => {
    const threshold = parseInt(newThreshold)
    if (isNaN(threshold) || threshold < 0) {
      enqueueSnackbar('Please enter a valid threshold', { variant: 'error' })
      return
    }

    setIsUpdating(true)
    try {
      await InventoryService.updateStockThreshold(variantId, {
        minimum_stock: threshold,
      })
      enqueueSnackbar('Stock threshold updated successfully', { variant: 'success' })
      setEditingThreshold(null)
      onRefresh()
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.detail || 'Failed to update threshold',
        { variant: 'error' }
      )
    } finally {
      setIsUpdating(false)
    }
  }

  const handleCancelEdit = () => {
    setEditingThreshold(null)
    setNewThreshold('')
  }

  const sortedVariants = [...lowStockVariants].sort((a, b) => {
    // Sort by stock deficit (most critical first)
    return b.stock_deficit - a.stock_deficit
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <WarningIcon color="warning" />
          <Typography variant="h6">Low Stock Alerts</Typography>
          <Chip
            label={lowStockVariants.length}
            color="warning"
            size="small"
          />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Variants that are running low on stock
        </Typography>
      </DialogTitle>

      <DialogContent>
        {lowStockVariants.length === 0 ? (
          <Alert severity="success">
            <Typography variant="body2">
              Great! No variants are currently running low on stock.
            </Typography>
          </Alert>
        ) : (
          <Stack spacing={2}>
            <Alert severity="warning">
              <Typography variant="body2">
                {lowStockVariants.length} variant{lowStockVariants.length !== 1 ? 's' : ''} need attention.
                Consider restocking or adjusting minimum stock thresholds.
              </Typography>
            </Alert>

            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {sortedVariants.map((variant, index) => (
                <React.Fragment key={variant.variant_id}>
                  <ListItem alignItems="flex-start">
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body1" fontWeight={600}>
                            {variant.product_name}
                          </Typography>
                          {variant.available_stock <= 0 && (
                            <Chip
                              label="Out of Stock"
                              color="error"
                              size="small"
                            />
                          )}
                        </Stack>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {variant.variant_name} • SKU: {variant.variant_sku}
                          </Typography>
                          {variant.category_name && (
                            <Typography variant="caption" color="text.secondary">
                              Category: {variant.category_name}
                            </Typography>
                          )}
                          
                          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Current Stock
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {variant.current_stock}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Reserved
                              </Typography>
                              <Typography variant="body2" color="warning.main">
                                {variant.reserved_stock}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Available
                              </Typography>
                              <Typography
                                variant="body2"
                                color={variant.available_stock <= 0 ? 'error.main' : 'text.primary'}
                                fontWeight={600}
                              >
                                {variant.available_stock}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Min Stock
                              </Typography>
                              {editingThreshold === variant.variant_id ? (
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={newThreshold}
                                    onChange={(e) => setNewThreshold(e.target.value)}
                                    inputProps={{ min: 0 }}
                                    sx={{ width: 80 }}
                                  />
                                  <Button
                                    size="small"
                                    onClick={() => handleSaveThreshold(variant.variant_id)}
                                    disabled={isUpdating}
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    size="small"
                                    onClick={handleCancelEdit}
                                    disabled={isUpdating}
                                  >
                                    Cancel
                                  </Button>
                                </Stack>
                              ) : (
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                  <Typography variant="body2">
                                    {variant.minimum_stock}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditThreshold(variant)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              )}
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Deficit
                              </Typography>
                              <Typography variant="body2" color="error.main" fontWeight={600}>
                                -{variant.stock_deficit}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Stack alignItems="center" spacing={0.5}>
                        <TrendingDownIcon
                          color={variant.available_stock <= 0 ? 'error' : 'warning'}
                        />
                        <Typography
                          variant="caption"
                          color={variant.available_stock <= 0 ? 'error.main' : 'warning.main'}
                          fontWeight={600}
                        >
                          {variant.available_stock <= 0 ? 'CRITICAL' : 'LOW'}
                        </Typography>
                      </Stack>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < sortedVariants.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onRefresh} disabled={isUpdating}>
          Refresh
        </Button>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default LowStockAlertsDialog